import React, { useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { cn } from '@/utils/cn';
import { Button, GlassRegular, IconButton } from '@/components/common';
import { Icon } from '@/components/common/Icon';
import { ScrollArea } from '@/components/common/ScrollArea';
import { NotificationCard } from './NotificationCard';
import { markAllRead } from '../utils/notification-actions';
import { getScopeFromNotificationRoute } from '../utils/notification-routing';
import { useAppStore } from '@/store/useAppStore';
import type { AppNotification } from '../types';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  notifications: AppNotification[];
  loading: boolean;
  onNavigate: (path: string) => void;
  sidebarCollapsed: boolean;
  isMobile?: boolean;
}

export const NotificationTray: React.FC<Props> = ({
  isOpen, onClose, notifications, loading, onNavigate, sidebarCollapsed, isMobile = false
}) => {
  const user = useAppStore(state => state.auth.user);
  const orgId = useAppStore(state => state.cloud.orgId);
  const projectId = useAppStore(state => state.cloud.projectId);
  const panelRef = useRef<HTMLDivElement>(null);
  const unreadCount = notifications.filter(n => !n.read).length;
  const panelStyle: React.CSSProperties = isMobile
    ? { borderRadius: '0 12px 12px 0' }
    : { left: sidebarCollapsed ? '76px' : '268px' };

  // Keyboard: Escape to close
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  // Focus trap: focus panel on open
  useEffect(() => {
    if (isOpen) panelRef.current?.focus();
  }, [isOpen]);

  // Navigate handler: close tray then navigate
  const handleNavigate = (path: string) => {
    const scope = getScopeFromNotificationRoute(path);
    if (scope) {
      const { metaActions } = useAppStore.getState();
      metaActions.setProject(scope);
      metaActions.setCloudState({
        enabled: true,
        orgId: scope.orgId,
        projectId: scope.projectId,
        role: null,
        member: null,
        lastSync: null,
        isBeta: undefined,
        scriptTextAccess: undefined,
        reportDistribution: undefined,
        communicationAudiences: undefined,
      });
    }

    onClose();
    onNavigate(path);
  };

  const handleMarkAllRead = async () => {
    if (user?.uid && orgId && projectId) {
      await markAllRead(user.uid, { orgId, projectId });
    }
  };

  return (
    <AnimatePresence>
      {isOpen ? <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className={cn(
              isMobile
                ? 'fixed inset-0 bg-black/40 z-50'
                : 'absolute inset-0 bg-black/30 z-14',
            )}
            onClick={onClose}
            aria-hidden
          />

          {/* Panel — slides from the left edge on phones and from beneath the sidebar on desktop */}
          <GlassRegular
            as={motion.div}
            ref={panelRef}
            tabIndex={-1}
            role="dialog"
            aria-label="Notifications"
            initial={{ x: '-100%', opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: '-100%', opacity: 0 }}
            transition={{ type: 'spring', bounce: 0, duration: 0.3 }}
            className={cn(
              isMobile
                ? 'fixed inset-y-0 left-0 w-80 max-w-[calc(100vw-2rem)] z-51'
                : 'absolute top-3 bottom-3 w-80 max-w-[90vw] z-15',
              'flex flex-col overflow-hidden'
            )}
            style={panelStyle}
          >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-border-dark">
          <div className="flex items-center gap-2">
            <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Notifications</h2>
            {unreadCount > 0 && (
              <span className="text-xs bg-primary/10 text-primary px-1.5 py-0.5 rounded-full">
                {unreadCount}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1">
            {unreadCount > 0 && (
              <Button onClick={() => { void handleMarkAllRead(); }} variant="link" size="none" className="text-xs text-primary hover:underline">
                Mark all read
              </Button>
            )}
            <IconButton onClick={onClose} icon="close" label="Close notifications" className="min-h-[44px] min-w-[44px] flex items-center justify-center rounded hover:bg-gray-100 dark:hover:bg-white/10" />
          </div>
        </div>

        {/* Notification list */}
        <ScrollArea className="flex-1 min-h-0">
          {loading ? (
            <div className="p-8 text-center text-sm text-gray-400">Loading...</div>
          ) : notifications.length === 0 ? (
            <div className="p-8 text-center">
              <Icon name="notifications_none" size="xl" className="text-gray-300 dark:text-gray-600 mx-auto mb-2" />
              <p className="text-sm text-gray-400">No notifications</p>
            </div>
          ) : (
            notifications.map(n => (
              <NotificationCard key={n.id} notification={n} onNavigate={handleNavigate} />
            ))
          )}
        </ScrollArea>
      </GlassRegular>
      </> : null}
    </AnimatePresence>
  );
};
